import React, { useEffect, useId, useRef, useState } from 'react';
import { AnimatePresence, motion, useAnimationControls } from 'framer-motion';
import { EyeIcon, EyeOffIcon, LockIcon, LockOpenIcon, XIcon } from 'lucide-react';
import { Logo } from '../Logo';
import { useCaseStudyAccess } from '../../contexts/CaseStudyAccessContext';
import { profile } from '../../data/profile';
import { EASE_OUT } from '../../utils/motion';
type PasswordModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
};
export function PasswordModal({
  open,
  onClose,
  onSuccess
}: PasswordModalProps) {
  return <AnimatePresence data-id="element-131" data-mp="2adx8h2l44hgz">
      {open && <PasswordDialog key="password-dialog" onClose={onClose} onSuccess={onSuccess} data-id="element-132" data-mp="2adoz3ko77uaj" />}
    </AnimatePresence>;
}
type PasswordDialogProps = Omit<PasswordModalProps, 'open'>;
function PasswordDialog({
  onClose,
  onSuccess
}: PasswordDialogProps) {
  const {
    verify,
    grantAccess
  } = useCaseStudyAccess();
  const [value, setValue] = useState('');
  const [visible, setVisible] = useState(false);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const timer = useRef<number>();
  const shake = useAnimationControls();
  const titleId = useId();
  const descId = useId();
  const inputId = useId();
  const errorId = useId();
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusTimer = window.setTimeout(() => inputRef.current?.focus(), 30);
    return () => {
      window.clearTimeout(focusTimer);
      window.clearTimeout(timer.current);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, []);
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key !== 'Tab' || !dialogRef.current) return;
    const focusable = dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), input, a[href]');
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (success) return;
    if (verify(value)) {
      setError(false);
      setSuccess(true);
      timer.current = window.setTimeout(() => {
        grantAccess();
        onSuccess();
      }, 380);
      return;
    }
    setError(true);
    shake.start({
      x: [0, -8, 8, -5, 5, 0],
      transition: {
        duration: 0.28,
        ease: EASE_OUT
      }
    });
    inputRef.current?.focus();
  };
  return <motion.div className="fixed inset-0 z-[60] flex items-center justify-center bg-night/50 p-4 backdrop-blur-sm" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2,
    ease: EASE_OUT
  }} onMouseDown={e => {
    if (e.target === e.currentTarget) onClose();
  }} data-id="element-133" data-mp="xw0expzmngrd">
      <motion.div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={descId} onKeyDown={handleKeyDown} initial={{
      opacity: 0,
      scale: 0.96,
      y: 8
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.96,
      y: 8
    }} transition={{
      duration: 0.2,
      ease: EASE_OUT
    }} className="relative w-full max-w-[420px] rounded-[28px] bg-canvas px-6 pb-7 pt-9 text-center shadow-2xl sm:px-9" data-id="element-134" data-mp="f6ke11k15maf">
        <button type="button" onClick={onClose} aria-label="Close" className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-full text-fg-faint transition-colors duration-150 hover:bg-surface hover:text-fg" data-id="element-135" data-mp="3nmnk52vhv2wn">
          <XIcon className="h-5 w-5" data-id="element-136" data-mp="2zw8a7uefyzm5" />
        </button>

        <div className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-coral-soft" data-id="element-137" data-mp="2zuna7p9cyogw">
          <Logo className="h-11 w-11" data-id="element-138" data-mp="34p1c8aiiztuj" />
        </div>

        <h2 id={titleId} className="mt-5 text-[28px] font-semibold tracking-tight text-ink" data-id="element-139" data-mp="39l89zg0rpz1e">
          Psst… it's locked
        </h2>
        <p id={descId} className="mx-auto mt-2 max-w-xs text-[15px] leading-relaxed text-fg-muted" data-id="element-140" data-mp="3enh3qelgt54k">
          This project is password protected. Pop in the secret word and come on in!
        </p>

        <form onSubmit={handleSubmit} className="mt-7 text-left" noValidate data-id="element-141" data-mp="3njyh7ohaxf3h">
          <label htmlFor={inputId} className="text-[11px] font-semibold uppercase tracking-[0.12em] text-fg-faint" data-id="element-142" data-mp="1oa8cyjn1wgz6">
            Password
          </label>
          <motion.div animate={shake} className="relative mt-2" data-id="element-143" data-mp="1lo8u69do6zzj">
            <input ref={inputRef} id={inputId} type={visible ? 'text' : 'password'} value={value} onChange={e => {
            setValue(e.target.value);
            if (error) setError(false);
          }} placeholder="Enter password" autoComplete="current-password" aria-invalid={error} aria-describedby={error ? errorId : undefined} className={`w-full rounded-full border bg-white py-3 pl-5 pr-12 text-[15px] text-fg placeholder:text-fg-faint transition-[border-color,box-shadow] duration-150 focus:outline-none focus:ring-4 ${error ? 'border-rust/60 focus:ring-coral-soft' : 'border-line focus:border-ink/40 focus:ring-ink-soft'}`} data-id="element-144" data-mp="1lqlj56huiam3" />
            <button type="button" onClick={() => setVisible(v => !v)} aria-label={visible ? 'Hide password' : 'Show password'} aria-pressed={visible} className="absolute right-2 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-full text-fg-faint transition-colors duration-150 hover:text-ink" data-id="element-145" data-mp="f8oqffspl9sn">
              {visible ? <EyeOffIcon className="h-[18px] w-[18px]" data-id="element-146" data-mp="bpzk16rgj8xn" /> : <EyeIcon className="h-[18px] w-[18px]" data-id="element-147" data-mp="boo7oijy4sux" />}
            </button>
          </motion.div>

          <div className="min-h-[24px] pt-2" aria-live="polite" data-id="element-148" data-mp="3u3762q66bos8">
            <AnimatePresence data-id="element-149" data-mp="2qw2i77xv1l9">
              {error && <motion.p id={errorId} role="alert" initial={{
              opacity: 0,
              y: -4
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.15,
              ease: EASE_OUT
            }} className="pl-5 text-xs font-medium text-rust" data-id="element-150" data-mp="3k6c9wdxdi0h4">
                  That's not it — try again or ask me.
                </motion.p>}
            </AnimatePresence>
          </div>

          <button type="submit" disabled={value.length === 0 && !success} className="mt-2 flex w-full items-center justify-center gap-2 rounded-full bg-ink py-3.5 text-[15px] font-semibold text-white transition-[background-color,transform,opacity] duration-150 ease-out hover:bg-ink-deep active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60" data-id="element-151" data-mp="2rk47bxp6325a">
            <AnimatePresence mode="popLayout" initial={false} data-id="element-152" data-mp="3aj8cgvdl8bro">
              <motion.span key={success ? 'open' : 'closed'} initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.8
            }} transition={{
              duration: 0.15,
              ease: EASE_OUT
            }} data-id="element-153" data-mp="3agvw2mnha9gq">
                {success ? <LockOpenIcon className="h-[18px] w-[18px]" data-id="element-154" data-mp="3t9gbb1a98g22" /> : <LockIcon className="h-[18px] w-[18px]" data-id="element-155" data-mp="3tarf313p9nt6" />}
              </motion.span>
            </AnimatePresence>
            {success ? 'Come on in' : 'Unlock the door'}
          </button>
        </form>

        <a href={`mailto:${profile.email}?subject=${encodeURIComponent('Portfolio password')}`} className="mt-6 inline-block text-sm text-fg-muted underline decoration-dotted decoration-fg-faint underline-offset-4 transition-colors duration-150 hover:text-ink hover:decoration-ink" data-id="element-156" data-mp="18g1iq0i61rbk">
          Forgot the magic word? Ask me
        </a>
      </motion.div>
    </motion.div>;
}